import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Briefcase, Heart, PenLine, Store, Wallet, PiggyBank, Scale, Gem, Zap, Sparkles, PhoneCall, Layers, Sprout, Users, TrendingUp, Rocket, ArrowUpRight, Star, RotateCcw } from "lucide-react";
import EmailOptin from "../components/site/EmailOptin";
import { MaskedLines, FadeUp } from "../components/site/Reveal";
import { PLATFORMS, getPlatform } from "../data/platforms";

const QUESTIONS = [
  {
    q: "What best describes your business?",
    options: [
      { label: "Ecommerce store", icon: ShoppingBag, w: { getresponse: 2, moosend: 2, mailerlite: 1 } },
      { label: "Agency or freelancer", icon: Briefcase, w: { getresponse: 2, moosend: 2, aweber: 1 } },
      { label: "Nonprofit", icon: Heart, w: { mailerlite: 2, getresponse: 2, aweber: 1 } },
      { label: "Creator / newsletter", icon: PenLine, w: { convertkit: 3, mailerlite: 1 } },
      { label: "Other small business", icon: Store, w: { getresponse: 1, aweber: 1, mailerlite: 1 } },
    ],
  },
  {
    q: "What's your budget comfort zone?",
    options: [
      { label: "Free as long as possible", icon: PiggyBank, w: { convertkit: 2, mailerlite: 2 } },
      { label: "Tight — under $15/mo", icon: Wallet, w: { mailerlite: 2, moosend: 2 } },
      { label: "Moderate — $20-60/mo", icon: Scale, w: { getresponse: 2, aweber: 1 } },
      { label: "Whatever works best", icon: Gem, w: { getresponse: 1, aweber: 1, convertkit: 1 } },
    ],
  },
  {
    q: "What matters most to you?",
    options: [
      { label: "Powerful automation", icon: Zap, w: { moosend: 2, getresponse: 2 } },
      { label: "Ease of use & design", icon: Sparkles, w: { mailerlite: 2, convertkit: 1 } },
      { label: "Human support", icon: PhoneCall, w: { aweber: 3, getresponse: 1 } },
      { label: "Everything in one tool", icon: Layers, w: { getresponse: 3 } },
    ],
  },
  {
    q: "How big is your list right now?",
    options: [
      { label: "Starting from zero", icon: Sprout, w: { convertkit: 1, mailerlite: 1 } },
      { label: "Under 2,500", icon: Users, w: { mailerlite: 1, moosend: 1 } },
      { label: "2,500 – 10,000", icon: TrendingUp, w: { getresponse: 1, moosend: 1 } },
      { label: "10,000+", icon: Rocket, w: { moosend: 1, getresponse: 1, aweber: 1 } },
    ],
  },
];

const RESULT_BLURBS = {
  getresponse: "You need range: automation, landing pages, funnels, and webinars without stacking five subscriptions. GetResponse covers all of it at a small-business price — our #1 overall pick.",
  aweber: "You value help over horsepower. AWeber's 24/7 phone-and-chat support, free migration, and top-tier deliverability make it the safest pair of hands in the industry.",
  mailerlite: "You want maximum capability per dollar with an interface that stays out of your way. MailerLite is the value champion — and its free plan (1,000 subscribers) is genuinely usable.",
  moosend: "You think in workflows. Moosend delivers enterprise-grade automation and the deepest reporting in its class, starting at $9/month.",
  convertkit: "You're building an audience business. Kit's creator-first design, 10,000-subscriber free plan, built-in commerce, and Creator Network are purpose-built for you.",
};

const QuizPage = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const done = answers.length === QUESTIONS.length;

  useEffect(() => { document.title = "Which Email Platform Fits You? 60-Second Quiz"; window.scrollTo(0, 0); }, []);

  const result = useMemo(() => {
    if (!done) return null;
    const scores = {};
    PLATFORMS.forEach((p) => { scores[p.slug] = 0; });
    answers.forEach((a) => Object.entries(a.w).forEach(([slug, pts]) => { scores[slug] += pts; }));
    const ordered = PLATFORMS.map((p) => p.slug).sort((a, b) => scores[b] - scores[a]);
    return { winner: getPlatform(ordered[0]), runnerUp: getPlatform(ordered[1]), scores };
  }, [answers, done]);

  const answer = (opt) => {
    setAnswers([...answers, opt]);
    setStep(step + 1);
  };

  const restart = () => { setAnswers([]); setStep(0); };

  return (
    <main className="relative mx-auto flex min-h-screen max-w-3xl flex-col px-6 pt-40 pb-24" data-testid="quiz-page">
      <div className="pointer-events-none absolute -top-20 left-1/2 h-[360px] w-[560px] -translate-x-1/2 rounded-full bg-volt/[0.05] blur-[110px]" />
      <p className="overline-label mb-4">Platform Finder</p>
      <h1 className="font-display text-4xl font-black tracking-tighter md:text-5xl">
        <MaskedLines lines={["Which platform fits"]} />
        <MaskedLines delay={0.15} lines={["your business?"]} lineClassName="font-serif-accent font-medium italic text-volt" />
      </h1>

      <div className="mt-10 flex items-center gap-3" data-testid="quiz-progress">
        {QUESTIONS.map((_, i) => (
          <span key={i} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i < step ? "bg-volt" : "bg-white/10"}`} />
        ))}
        <span className="ml-2 font-serif-accent text-lg italic text-white/40">{Math.min(step + 1, 4)}/4</span>
      </div>

      <div className="relative mt-10 flex-1">
        <AnimatePresence mode="wait">
          {!done ? (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="font-display text-2xl font-bold" data-testid={`quiz-question-${step}`}>{QUESTIONS[step].q}</h2>
              <div className="mt-7 grid gap-3">
                {QUESTIONS[step].options.map((opt) => (
                  <button
                    key={opt.label}
                    onClick={() => answer(opt)}
                    data-testid={`quiz-option-${step}-${opt.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-cardink px-6 py-5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-volt/50 hover:bg-white/[0.04]"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-volt/10 transition-colors group-hover:bg-volt/20">
                      <opt.icon className="h-5 w-5 text-volt" />
                    </span>
                    <span className="font-display font-bold text-white/85 transition-colors group-hover:text-white">{opt.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          ) : result ? (
            <motion.div key="result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="quiz-result">
              <p className="overline-label !text-volt">Your Match</p>
              <div className="mt-5 rounded-2xl border border-volt/30 bg-cardink p-8">
                <div className="flex flex-wrap items-center gap-5">
                  <span className="flex h-16 w-16 items-center justify-center rounded-2xl font-display text-xl font-extrabold" style={{ backgroundColor: `${result.winner.hue}1f`, color: result.winner.hue }}>{result.winner.monogram}</span>
                  <div>
                    <h2 className="font-display text-3xl font-black tracking-tight" data-testid="quiz-winner-name">{result.winner.name}</h2>
                    <p className="mt-1 flex items-center gap-2 text-sm text-white/55">
                      <Star className="h-3.5 w-3.5 fill-volt text-volt" />{result.winner.rating} · {result.winner.badge} · from {result.winner.startingPrice}/mo
                    </p>
                  </div>
                </div>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/65">{RESULT_BLURBS[result.winner.slug]}</p>
                <div className="mt-7 flex flex-wrap items-center gap-4">
                  <a href={result.winner.affiliateUrl} target="_blank" rel="noopener noreferrer sponsored" data-testid="quiz-result-cta" className="flex items-center gap-2 rounded-full bg-volt px-7 py-3.5 text-sm font-bold text-ink transition-colors hover:bg-volt-hover">
                    Start {result.winner.name} Free <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <Link to={`/reviews/${result.winner.slug}`} data-testid="quiz-result-review" className="text-sm text-white/60 underline-offset-2 hover:text-volt hover:underline">Read the full review</Link>
                </div>
                <p className="mt-5 border-t border-white/[0.07] pt-4 text-sm text-white/45">
                  Runner-up: <Link to={`/reviews/${result.runnerUp.slug}`} data-testid="quiz-runnerup" className="font-semibold text-white/70 underline-offset-2 hover:text-volt hover:underline">{result.runnerUp.name}</Link> — worth a look if the first pick doesn't click.
                </p>
              </div>

              <div className="mt-10">
                <h3 className="font-display text-xl font-bold">Email me my result + the free checklist</h3>
                <div className="mt-4"><EmailOptin source="quiz" quizResult={result.winner.slug} compact /></div>
              </div>
              <button onClick={restart} data-testid="quiz-restart" className="mt-8 flex items-center gap-2 text-sm text-white/45 transition-colors hover:text-volt">
                <RotateCcw className="h-4 w-4" /> Retake the quiz
              </button>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </main>
  );
};

export default QuizPage;
